import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import { useLightbox } from "@/components/shell/Lightbox";
import { useMenu } from "@/components/shell/Menu";
import type { MenuItemSpec } from "@/components/shell/menuSpec";
import { mediaUrl } from "@/lib/note";
import { useWideLayout } from "@/lib/useWideLayout";
import type { ReviewCard, ReviewPosition } from "@/lib/review";
import { FLAGS as CARD_FLAGS } from "@/surfaces/cards/query";

import { CardFace } from "./CardFace";
import { DayBars } from "./DayBars";
import {
  actionForKey,
  idleCountdown,
  imageModeLabel,
  noteSlots,
  queueSummary,
  sittingPosition,
  SWIPE_MIN_PX,
  swipeAction,
  verticalSwipe,
  type ImageMode,
  type ReviewAction,
  EASY_SEAL_MS,
  easySealSeconds,
} from "./sessionRules";

/**
 * A sitting — the chrome bar, the card, and the two buttons (`5f` desktop, `6a`/`6b` phone).
 *
 * **Two buttons only.** Pass sends Anki's *Good*, Fail sends *Again*; Hard and Easy do not exist
 * here. The intervals beside them are printed because they are free information, not because
 * there is a decision to make.
 *
 * **A Pass can still land as Easy — by the server's rule, never by a button** (日本-700). The
 * session reports how long the first reveal took; when the server promotes the answer, the next
 * card arrives with `promotion` set and `EasySeal` stamps 簡 in the card region's corner.
 */

interface Props {
  position: ReviewPosition;
  isPhone: boolean;
  imageMode: ImageMode;
  imageModes: ImageMode[];
  busy: boolean;
  onImageMode: (mode: ImageMode) => void;
  /** `revealMs` is card shown → first reveal (日本-700); null only if the card was never revealed. */
  onAnswer: (card: ReviewCard, passed: boolean, tookMs: number, revealMs: number | null) => void;
  onUndo: () => void;
  onEnd: () => void;
  onEdit: (card: ReviewCard) => void;
  onSuspend: (card: ReviewCard, wholeNote: boolean) => void;
  onFlag: (card: ReviewCard, flag: number) => void;
  onAddTag: (card: ReviewCard) => void;
  onInfo: (card: ReviewCard) => void;
  /** A grade that did not land. Drawn in the chrome's corner slot — see `SessionChrome`. */
  notice?: string | null;
  /**
   * The グループに追加 submenu for this card (日本-601), built by the caller from `groupMenuItems`
   * so 復習, a drill and 単語 cannot disagree about what a tick means. Absent draws no row.
   */
  groupItems?: (card: ReviewCard) => MenuItemSpec[];
  /**
   * **A drill, not a sitting** (日本-601). Present means nothing reaches the scheduler: the caller
   * grades locally, so there is no sync hold to guard and therefore **no idle kick-out**, the day
   * bars give way to the drill's own counter, and the grade keys carry no intervals — a drill
   * schedules nothing, so an interval would be a promise about a write that never happens.
   */
  drill?: DrillProgress;
}

export interface DrillProgress {
  /** The chosen groups' names, joined — where a sitting shows the deck. */
  title: string;
  passed: number;
  total: number;
  /** Misses still waiting to come back — `再出 N`, in red because it is what wants you. */
  repeats: number;
  /** Which showing of this card is on screen: 1, then `2回目`, `3回目`… after misses. */
  attempt: number;
}

/**
 * Anki's five user flags, read from `cards/query.ts` rather than written out again here.
 *
 * They were a second table, and the colour words stayed English through the Japanese pass while
 * Cards' identical five became 赤 / 橙 / 緑 / 青 / 桃 — one concept with two names, two surfaces
 * apart, which is exactly what `flagName`'s remarks say a single table exists to prevent. The
 * sixth row below is `フラグなし`, which is `flagName(0)`.
 */
const FLAGS = CARD_FLAGS.map((entry) => ({
  flag: entry.value,
  label: entry.label,
  color: `var(${entry.token})`,
}));

export function Session({
  position,
  isPhone,
  imageMode,
  imageModes,
  busy,
  onImageMode,
  onAnswer,
  onUndo,
  onEnd,
  onEdit,
  onSuspend,
  onFlag,
  onAddTag,
  onInfo,
  notice,
  groupItems,
  drill,
}: Props) {
  const card = position.card;
  const [revealed, setRevealed] = useState(false);
  const menu = useMenu();
  const lightbox = useLightbox();
  const wide = useWideLayout();

  /**
   * When the card on screen appeared. A ref rather than state: nothing renders from it, and making
   * it state would re-render the card every time the clock was read.
   *
   * It is the honest answer to `tookMs` — only this side knows when the card was drawn, which is
   * why the server takes the figure rather than measuring the gap between its own requests.
   */
  /**
   * The idle deadline, and the second that is drawn from it.
   *
   * **A sitting holds the collection's syncs** so `↑` can take an answer back, so leaving one open
   * indefinitely holds the whole app back from syncing. The server enforces the same window as its
   * own hold deadline and would flush without us; this is the half that tells the reader it is
   * about to happen and returns them somewhere sensible rather than to a card that has stopped
   * being answerable.
   *
   * `secondsLeft` ticks once a second and only *renders* in the last minute — see `idleCountdown`.
   */
  const idleSeconds = Math.max(60, position.idleMinutes * 60);
  const [secondsLeft, setSecondsLeft] = useState(idleSeconds);
  const bump = useCallback(() => setSecondsLeft(idleSeconds), [idleSeconds]);

  useEffect(() => {
    // A drill holds nothing, so there is nothing to time out of — see `Props.drill`.
    if (drill) return;
    const tick = window.setInterval(() => {
      setSecondsLeft((left) => {
        if (left <= 1) {
          // `onEnd` is the same path Esc takes, so the flush and the return happen once rather than
          // in two places that could disagree.
          onEnd();
          return idleSeconds;
        }
        return left - 1;
      });
    }, 1000);
    return () => window.clearInterval(tick);
  }, [idleSeconds, onEnd, drill !== undefined]);

  /**
   * **One audio element for the whole sitting** (日本-347).
   *
   * `play` used to be `new Audio(url).play()` and kept no reference, so nothing could pause the last
   * one: five taps on `word` were five overlapping voices, and tapping `word` then `sentence` played
   * both at once. With a single element that is unspellable — re-pointing `src` stops what was
   * sounding, so a click always *replaces* rather than joining.
   *
   * A ref rather than state: it is an object nothing renders from, and putting it in state would
   * re-render the card on every playback.
   */
  const player = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  const play = useCallback((filename: string) => {
    let audio = player.current;
    if (!audio) {
      audio = new Audio();
      // Attached once, on the element rather than per play: `src` is re-pointed on every click and
      // handlers hung at that moment would have to be torn down at exactly the right time.
      audio.onended = () => setPlaying(null);
      audio.onerror = () => setPlaying(null);
      player.current = audio;
    }
    audio.pause();
    audio.src = mediaUrl(filename);
    audio.currentTime = 0;
    // **Set before the play, not in a `then`.** The feedback has to appear on the press; a media
    // file that has not been fetched yet takes a moment, and that moment is exactly when the reader
    // is deciding whether the tap registered.
    setPlaying(filename);
    void audio.play().catch(() => {
      // A browser that refuses to play without a gesture is not a review failure. The row is still
      // there to click, which IS a gesture, so the second attempt works.
      setPlaying(null);
    });
  }, []);

  /**
   * **Stopped when the sitting goes, not only when the card changes.**
   *
   * The card-change effect below pauses the player, but it has no cleanup and so does not run on
   * unmount — and the element is a ref that nothing else can reach. So grading the last card,
   * pressing `Esc`, or leaving for another surface mid-playback left the word sounding across the
   * whole app with no way to stop it: the per-click `new Audio()` bug this file just fixed, back at
   * component granularity instead of click granularity.
   */
  useEffect(
    () => () => {
      player.current?.pause();
      player.current = null;
    },
    [],
  );

  const shownAt = useRef(Date.now());
  /**
   * When this card was FIRST revealed, or null (日本-700). **Not reset by `↓`**, on `shownAt`'s
   * reasoning: the Easy rule asks how long recall took, and hiding the answer to look again does
   * not make the first recall any slower. A ref for the same reason `shownAt` is one.
   */
  const revealedAt = useRef<number | null>(null);
  /**
   * **A layout effect, not an ordinary one** (日本-347).
   *
   * `position` arrives from an awaited promise, so the next card renders while `revealed` is still
   * true — and after paint is too late: there is a frame in which the *next* card's answer is drawn,
   * in the same scroll container, at the previous card's `scrollTop`. That is the flicker behind
   * "the scrollable region seems temperamental". `useLayoutEffect` runs before the browser paints,
   * so the frame never exists.
   */
  useLayoutEffect(() => {
    setRevealed(false);
    shownAt.current = Date.now();
    revealedAt.current = null;
    bump();
    // A card's audio must not play over the next question. `pause` rather than dropping the element:
    // it is reused for the whole sitting.
    player.current?.pause();
    setPlaying(null);
    // **`reps` as well as the id, because a Fail hands back the SAME card.** That is Anki's *Again*
    // doing its job — `test_failing_a_card_brings_it_back` asserts it — and keyed on the id alone
    // this effect does not re-run: the answer stays on screen with no chance to recall it, and
    // `shownAt` keeps the first showing's timestamp so `tookMs` spans both. Routine rather than
    // exotic: it is every failed card in the tail of every session. `reps` moves on every answer.
  }, [bump, card?.cardId, card?.reps]);

  /**
   * **The one door to the answer**, so autoplay cannot be reached by one route and missed by three.
   *
   * Every reveal path funnels here: tapping the card, the footer button, `Space`/`Enter`, and the
   * first press of a grading key.
   *
   * **Played from the handler rather than from an effect**, and that is load-bearing. Every reveal is
   * a real user gesture, so `play()` called inside it is inside that gesture and permitted; from an
   * effect it depends on Chrome's sticky activation, which fails in a way indistinguishable from
   * broken audio.
   */
  const reveal = useCallback(() => {
    if (revealedAt.current === null) revealedAt.current = Date.now();
    setRevealed(true);
    if (position.autoplayWord && card?.audio) play(card.audio);
  }, [card?.audio, play, position.autoplayWord]);

  /**
   * Put the answer away again — `↓`, and nothing else (Alex, 11 Aug 2026).
   *
   * **`shownAt` is deliberately not reset.** `tookMs` is how long the reader spent on this card, and
   * looking, hiding and looking again is all of it — restarting the clock would report the last
   * glance and quietly under-count every card that was thought about twice. Same argument the
   * card-change effect makes for a failed card coming back with the timestamp of its first showing.
   *
   * The audio stops, because what was sounding belongs to an answer that is no longer on screen.
   */
  const unreveal = useCallback(() => {
    setRevealed(false);
    player.current?.pause();
    setPlaying(null);
  }, []);

  /**
   * A notetype this app has no layout for. It is drawn as a named error, and **grading is refused**
   * — the docstrings on `Layout` and `CardFace` both promise that, and until this it was only ever
   * said rather than done, so a `Kanji (Apoc)` card could be graded blind on a screen showing no
   * Japanese at all. `Card ⋯` and `Esc` keep working, which is what the error copy tells you to use.
   */
  const gradable = card !== null && card.layout !== null;

  /**
   * Open a card image in the shared lightbox (日本-350).
   *
   * **It refuses when the touch that produced this click was a swipe.** The card region owns the
   * grading gestures and an image fills much of it, so a left-swipe beginning on the picture arrives
   * as a fail *and* a click — and a browser will not reliably suppress the second. `wasTap` is set
   * on `touchend` a moment before this runs. A pointer device never sets it false, so a mouse click
   * always opens.
   *
   * The keys are already safe by a different mechanism: the lightbox holds a capture-phase
   * `stopImmediatePropagation` at `window`, which beats this component's own `keydown`. Both are
   * true by construction and neither is obvious, which is why `check-review.mjs` asserts them.
   */
  const expandImage = useCallback(
    (filename: string) => {
      if (!wasTap.current) return;
      lightbox.open(filename);
    },
    [lightbox],
  );

  const answer = useCallback(
    (passed: boolean) => {
      if (!card || busy || card.layout === null) return;
      bump();
      const revealedMs =
        revealedAt.current === null ? null : revealedAt.current - shownAt.current;
      onAnswer(card, passed, Date.now() - shownAt.current, revealedMs);
    },
    [bump, busy, card, onAnswer],
  );

  const cardMenu = useCallback(
    (point: { x: number; y: number }) => {
      if (!card) return;
      const items: MenuItemSpec[] = [
        // **Only on the answer face, because that is the only place it means anything** — and it is
        // the whole of unreveal's on-screen presence. `↓` has no gesture (the vertical axis belongs
        // to scrolling a long card), so without a row here the key is undiscoverable: nothing on the
        // surface would say the answer can be put away again.
        ...(revealed
          ? ([
              {
                kind: "item",
                id: "hide",
                label: "答えを隠す",
                hint: "↓",
                onSelect: unreveal,
              },
              { kind: "separator", id: "sep-hide" },
            ] as MenuItemSpec[])
          : []),
        { kind: "caption", id: "flag-caption", label: "フラグ" },
        {
          kind: "swatches",
          id: "flags",
          swatches: FLAGS.map((entry) => ({
            id: `flag-${entry.flag}`,
            label: entry.label,
            color: entry.color,
            selected: card.flag === entry.flag,
            // Choosing the flag a card already has clears it, which is Anki's own behaviour and
            // the only way to unflag without a sixth swatch meaning "none".
            onSelect: () => onFlag(card, card.flag === entry.flag ? 0 : entry.flag),
          })),
          clear: {
            id: "flag-none",
            label: "フラグなし",
            selected: card.flag === 0,
            onSelect: () => onFlag(card, 0),
          },
        },
        { kind: "separator", id: "sep-1" },
        { kind: "item", id: "tag", label: "タグを追加…", hint: "T", onSelect: () => onAddTag(card) },
        ...(groupItems
          ? ([
              { kind: "submenu", id: "groups", label: "グループに追加", items: groupItems(card) },
            ] as MenuItemSpec[])
          : []),
        { kind: "item", id: "edit", label: "ノートを編集", hint: "E", onSelect: () => onEdit(card) },
        { kind: "separator", id: "sep-2" },
        {
          kind: "item",
          id: "suspend",
          label: "カードを停止",
          hint: "!",
          onSelect: () => onSuspend(card, false),
        },
        {
          kind: "item",
          id: "suspend-note",
          label: "ノート全体を停止",
          hint: "⇧!",
          danger: true,
          onSelect: () => onSuspend(card, true),
        },
        { kind: "separator", id: "sep-3" },
        { kind: "item", id: "info", label: "カード情報", hint: "I", onSelect: () => onInfo(card) },
      ];
      bump();
      menu.openAt(point, items, `review-card-${card.cardId}`);
    },
    [bump, card, groupItems, menu, onAddTag, onEdit, onFlag, onInfo, onSuspend, revealed, unreveal],
  );

  const run = useCallback(
    (action: ReviewAction) => {
      if (!card) return;
      switch (action) {
        case "reveal":
          reveal();
          break;
        case "unreveal":
          unreveal();
          break;
        case "fail":
          answer(false);
          break;
        case "pass":
          answer(true);
          break;
        case "undo":
          if (position.canUndo) onUndo();
          break;
        case "end":
          onEnd();
          break;
        case "edit":
          onEdit(card);
          break;
        case "suspend":
          onSuspend(card, false);
          break;
        case "info":
          onInfo(card);
          break;
        case "tag":
          onAddTag(card);
          break;
        case "menu":
          cardMenu({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
          break;
        case "groups":
          // Straight to the group list rather than to `Card ⋯` with the submenu to find — the key
          // exists so adding the card you just missed costs one press and one pick.
          if (groupItems) {
            menu.openAt(
              { x: window.innerWidth / 2, y: window.innerHeight / 2 },
              groupItems(card),
              `review-groups-${card.cardId}`,
            );
          }
          break;
      }
    },
    [
      answer,
      card,
      cardMenu,
      groupItems,
      menu,
      onAddTag,
      onEdit,
      onEnd,
      onInfo,
      onSuspend,
      onUndo,
      position.canUndo,
      reveal,
      unreveal,
    ],
  );

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      // **A menu is open — and `Menu` will not stop this for us.** It swallows Escape in the
      // capture phase and nothing else; its own arrow navigation is a React `onKeyDown`, which
      // React 19 dispatches at the root container, *below* this window listener. So without the
      // check, `↑` inside the flag row also undoes the previous answer and `←`/`→` grade the card
      // behind the scrim. `openKey` is what the `⋯` already reads to render `aria-expanded`.
      //
      // Dialogs need no equivalent: `DialogsProvider` blanket-swallows keys in capture.
      if (menu.openKey !== null) return;

      // Or the reader is typing. `isTextEntry` is not enough on its own here: a menu row is a
      // button, which is why the check above is separate rather than folded into this one.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      ) {
        return;
      }
      const action = actionForKey(event, { revealed });
      if (!action) return;
      event.preventDefault();
      bump();
      run(action);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [bump, menu.openKey, revealed, run]);

  // --- swipes ---------------------------------------------------------------------------------

  /**
   * **Touch events, not pointer events, and that is what makes swiping work at all.**
   *
   * The first version listened for `pointerdown`/`pointerup` on this container. Both fire, but the
   * card region contains a full-width `<button>` — the tap-to-reveal target — and on a touch screen
   * a drag that starts on a button is claimed by it: the browser fires `pointercancel`, not
   * `pointerup`, so the release never arrived and no swipe was ever detected. Nothing errored; the
   * gesture simply did nothing, which is exactly how it was reported.
   *
   * `touchstart`/`touchend` are not subject to that: they report the finger regardless of what it
   * started on. `touchend.changedTouches[0]` is where it left, and the ratio guard in
   * `swipeAction` is what keeps a scroll from grading a card.
   */
  const dragFrom = useRef<{ x: number; y: number } | null>(null);
  /**
   * Whether the touch that is ending was a tap rather than a drag (日本-350).
   *
   * A swipe that **starts on an image** must still grade the card, and a tap on one must open the
   * lightbox — but a browser does not reliably suppress the `click` that follows a touch drag, so
   * without this every left-swipe beginning on the picture would fail the card *and* open the
   * viewer over it. Set on `touchend`, read by the image's own click a moment later; the shape is
   * `BottomSheet.tsx`'s `moved` flag.
   *
   * A pointer device never sets it, which is why it starts true: there is no drag to confuse a
   * click with when there is no touch.
   */
  const wasTap = useRef(true);
  const onTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0];
    wasTap.current = true;
    dragFrom.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
  };
  const onTouchEnd = (event: React.TouchEvent) => {
    const from = dragFrom.current;
    dragFrom.current = null;
    const touch = event.changedTouches[0];
    if (!from || !touch) return;
    const dx = touch.clientX - from.x;
    const dy = touch.clientY - from.y;
    // Measured against the same threshold the swipe guards use, so "not a tap" and "a swipe" cannot
    // disagree about one gesture and leave a drag that neither grades nor opens anything.
    wasTap.current = Math.abs(dx) < SWIPE_MIN_PX && Math.abs(dy) < SWIPE_MIN_PX;

    // **The question face reads both axes; the answer face reads only sideways.**
    //
    // On the question there is one line of Japanese in a box that does not scroll, so a vertical
    // drag is unambiguous and gets `↑`'s meaning: undo. Sideways reveals, exactly as `←`/`→` do —
    // this is narrower than 日本-347's "any direction reveals", and deliberately so (Alex, 11 Aug
    // 2026): the vertical axis now means something, and it cannot mean two things.
    //
    // On the answer, vertical is how a long card is read. Nothing may take it — which is why
    // un-revealing is `↓` and no gesture at all.
    if (!revealed) {
      if (swipeAction(dx, dy)) reveal();
      else if (verticalSwipe(dx, dy) === "up") run("undo");
      return;
    }
    if (!gradable) return;
    const action = swipeAction(dx, dy);
    if (action) run(action);
  };

  if (!card) return null;

  /**
   * **The answer face widens; the header and the action bar keep the question's width** (日本-416).
   *
   * This is the one computed width on the surface. 日本-350 gave all three rows a shared
   * `--review-measure` on the argument that three hand-copied literals would drift apart — they
   * would, and that variable is gone anyway. What it bought was worth less than what it cost,
   * because the shared measure then *moved*: measured at 1920, revealing took every row from 1320
   * to 1760, which took the two day-bar tracks from 1050px to 1490px **for fractions that had not
   * changed**, and each grade button to 832px, on every card and back again on the grade. Alex's
   * call, 22 Aug 2026.
   *
   * The header and the footer now carry `max-w-[var(--content-max)]` — the same literal
   * `Landing.tsx` and `Setup.tsx` carry, because that cap is a property of the surface rather than
   * of this sitting.
   *
   * **The WHOLE back goes wide, text column included** (Alex, asked directly, 22 Aug 2026). So the
   * reading column takes `--content-max-wide` along with the images, and at 1920 the headword's
   * left edge lands ~220px outside 不可's. A real alignment is being given up: the footer's
   * `md:px-10` and the split arm's `px-10` are the same 40px gutter, so while the two shared a cap
   * the button row's left edge sat exactly on the answer text's. It will be rediscovered as a bug.
   * It is the trade — do not re-cap the text column, and do not re-widen the footer to line them up.
   *
   * **This is a THIRD arrangement, and neither thing it sits between is a restoration.** 日本-350's
   * shared measure is one. The design frames are the other, in the opposite direction: handoff
   * frame `1c` caps *nothing* — the 72px chrome is `padding: 0 26px` full-bleed, the footer
   * `0 40px 34px` — and its README says the footer "spans the full width beneath both columns".
   * `--content-max-wide` appears in no design artifact at all; it was chosen in `f05f4d4`.
   *
   * What was given up is now the check's job rather than the type system's: the three rows can
   * drift by hand, so `check-review.mjs` measures them either side of one reveal and asserts that
   * the two which must hold still did, that only this one moved, and that they stay concentric.
   *
   * **It follows `answerLayout` rather than second-guessing it.** Both used to test the image count
   * and had to agree about it by hand; since the split stopped depending on what the card carries
   * (Alex, 16 Aug 2026) the only thing left to check is that there is a face to widen for — an
   * unrevealed question is one word in a box, and a notetype with no layout is an error message.
   *
   * `card` is already narrowed by the early return above, so this asks only about the layout.
   */
  const cardMeasure =
    wide && card.layout !== null && revealed
      ? "var(--content-max-wide)"
      : "var(--content-max)";

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {drill ? (
        <DrillChrome
          drill={drill}
          isPhone={isPhone}
          onEnd={onEnd}
          onMenu={cardMenu}
          notice={notice}
        />
      ) : (
        <SessionChrome
          position={position}
          isPhone={isPhone}
          onEnd={onEnd}
          onMenu={cardMenu}
          secondsLeft={secondsLeft}
          notice={notice}
        />
      )}

      <div
        data-check="review.card-region"
        className="relative mx-auto flex min-h-0 w-full flex-1 flex-col"
        style={{ maxWidth: cardMeasure }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onContextMenu={(event) => {
          event.preventDefault();
          cardMenu({ x: event.clientX, y: event.clientY });
        }}
      >
        {/* BEHIND the card, not over it. Drawn after `CardFace` in DOM order they paint on top,
            and at 390px two 88px gradients cover 45% of the width — measured on a real phone
            viewport, the gloss came out washed pink on the left and green on the right, which reads
            as a rendering bug rather than as a legend for a gesture. `z-0`/`z-10` puts them under,
            and 76px is the frame's own width. */}
        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          {drill && drill.attempt > 1 && (
            <span
              data-check="drill.attempt"
              className="absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-[var(--r-2)] bg-accent-tint px-2 py-[2px] text-meta text-accent-text"
            >
              {drill.attempt}回目
            </span>
          )}
          {!drill && <EasySeal position={position} isPhone={isPhone} />}
          <CardFace
            card={card}
            revealed={revealed}
            isPhone={isPhone}
            imageMode={imageMode}
            onReveal={reveal}
            onPlay={play}
            playing={playing}
            onExpand={expandImage}
            // The same `wasTap` guard `expandImage` reads, handed to the examples toggle — that
            // control sits inside the region whose sideways swipe grades the card, and a browser
            // does not reliably suppress the `click` after a touch drag.
            wide={wide}
          />
        </div>

        {/* **No edge gradients.** The design draws persistent `← Fail` / `Pass →` tint strips and
            Alex removed them (9 Aug 2026): at 390px two of them cover 40% of the width, and even
            drawn behind the card they tint the answer. The gesture stays; only its decoration goes.
            The `Fail`/`Pass` buttons under the card carry the same two words. */}
      </div>

      <div
        data-check="review.actions"
        className="mx-auto flex w-full max-w-[var(--content-max)] flex-shrink-0 flex-col gap-3.5 px-[18px] pb-[22px] md:px-10 md:pb-[34px]"
      >
        {!revealed ? (
          <div className="flex gap-3">
            <button
              type="button"
              onClick={reveal}
              data-check="review.reveal"
              className="flex flex-1 flex-col items-center justify-center gap-1.5 rounded-[var(--r-4)] border border-line-strong bg-panel"
              style={{ height: isPhone ? 64 : 104 }}
            >
              <span className="font-semibold text-ink" style={{ fontSize: isPhone ? 19 : 25 }}>
                答えを見る
              </span>
              {!isPhone && (
                <span className="font-mono text-secondary text-ink-3">
                  Spaceまたはカードをタップ
                </span>
              )}
            </button>
            {/* **Undo sits beside Show answer rather than in a row of its own** (Alex, 11 Aug 2026).
                It is the one action that belongs on a question — `Edit` and `Suspend` are about a
                card you have not looked at yet, and both already live in `Card ⋯`. A square keeps
                the reveal target nearly full width, and `↑` is drawn on it because the same gesture
                is a swipe up. */}
            {isPhone && (
              <button
                type="button"
                disabled={!position.canUndo}
                onClick={onUndo}
                aria-label="直前の解答を取り消す"
                data-check="review.undo"
                className="flex flex-col items-center justify-center gap-0.5 rounded-[var(--r-4)] border border-line-strong bg-panel px-4 text-ink-2 disabled:opacity-40"
                style={{ height: 64 }}
              >
                <span aria-hidden className="text-body leading-none">
                  ↑
                </span>
                <span className="text-meta font-semibold leading-none">取消</span>
              </button>
            )}
          </div>
        ) : (
          <div className="flex gap-4">
            <GradeButton
              tone="fail"
              label="不可"
              hint={gradeHint("←", card.failLabel, isPhone)}
              isPhone={isPhone}
              disabled={busy || !gradable}
              onClick={() => answer(false)}
            />
            <GradeButton
              tone="pass"
              label="合格"
              hint={gradeHint("→", card.passLabel, isPhone)}
              isPhone={isPhone}
              disabled={busy || !gradable}
              onClick={() => answer(true)}
            />
          </div>
        )}

        {!isPhone && (
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!position.canUndo}
              onClick={onUndo}
              data-check="review.undo"
              className="rounded-[var(--r-2)] border border-line-strong bg-panel px-[14px] py-[9px] text-secondary font-semibold text-ink-2 disabled:opacity-40"
            >
              取り消し ↑
            </button>
            {card.flag > 0 && (
              <span
                className="inline-flex items-center gap-1.5 rounded-[var(--r-2)] border px-3 py-[9px] text-secondary font-semibold"
                style={{
                  borderColor: "var(--accent-line)",
                  background: "var(--accent-tint)",
                  color: "var(--accent-text)",
                }}
              >
                <span
                  aria-hidden
                  className="h-2 w-2 rounded-[var(--r-2)]"
                  style={{ background: FLAGS.find((f) => f.flag === card.flag)?.color }}
                />
                フラグ付き
              </span>
            )}
            <div className="flex-1" />
            {revealed && (
              <div className="flex items-center gap-2">
                <span className="text-meta text-ink-3">画像</span>
                <div className="flex gap-[3px] rounded-[var(--r-2)] bg-sunken p-[3px]">
                  {imageModes.map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      // **The hook is the MODE, never the label** — `review.image.extra`, not
                      // `review.image.追加`. `imageModeLabel` exists precisely because the value is
                      // a key (it is also what `loadImageMode` persists), and a `data-check` built
                      // from the Japanese would be the third instance of this repo's recurring bug:
                      // renaming `review.audio.word` to `review.audio.単語` stopped five assertions
                      // running while the passing count still went up.
                      data-check={`review.image.${mode}`}
                      onClick={() => onImageMode(mode)}
                      className={
                        mode === imageMode
                          ? "rounded-[var(--r-2)] bg-panel px-2.5 py-1.5 text-meta font-semibold text-ink shadow-[var(--sh-frame)]"
                          : "rounded-[var(--r-2)] px-2.5 py-1.5 text-meta font-semibold text-ink-3"
                      }
                    >
                      {imageModeLabel(mode)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* `7d`: one `Card ⋯` replaces `5f`'s loose Edit and Suspend buttons, and the freed
                space is what the flag chip above uses. Every item keeps its own shortcut, so the
                menu is a reminder rather than a route.

                **It is drawn like every other button here, which the frames are not** (Alex, 11 Aug
                2026). The handoff gives this one control `#1a1917` for both border and label — the
                only bordered control in the whole surface drawn in ink, against `--line-strong` for
                `Undo last`, `Edit`, `Suspend`, `Custom study` and `Done`. The emphasis reads as
                deliberate in the mock, where it sits among those siblings; on the built row, beside
                `Undo last ↑` and the Images toggle, it reads as a mistake. Alex's call to match. */}
            <button
              type="button"
              data-check="review.card-menu"
              onClick={(event) => {
                const rect = event.currentTarget.getBoundingClientRect();
                cardMenu({ x: rect.right, y: rect.top });
              }}
              className="flex items-center gap-1.5 rounded-[var(--r-2)] border border-line-strong bg-panel px-[13px] py-[9px] text-secondary font-semibold text-ink-2"
            >
              カード ⋯
            </button>
          </div>
        )}

      </div>
    </div>
  );
}

/**
 * The 72px bar a sitting wears, and the one `7e` keeps (日本-389).
 *
 * Exported because the finished screen renders **inside the session frame** — the design's rule
 * that a session never dissolves on its own — so the bar has to outlive the card. One
 * implementation rather than two, or the two states drift on the day one of them gains a figure.
 *
 * **It was 50px and carried one bar scoped to the sitting since the last sync.** That bar is gone
 * rather than restyled, and the reason is that it could not be made honest: its denominator was
 * `answered + remaining`, so every failed card grew it and the fill visibly ran backwards — and
 * after a mid-day Anki desktop session it described a fraction of the day's work with no way of
 * knowing it. What is left of it is `この席 84枚目`, an ordinal that cannot lie.
 *
 * 72px because two bars, two fractions and their labels do not fit in 50.
 *
 * `onMenu` is absent on the finished screen: there is no card to act on.
 */
export function SessionChrome({
  position,
  isPhone,
  onEnd,
  onMenu,
  secondsLeft,
  notice,
}: {
  position: ReviewPosition;
  isPhone: boolean;
  onEnd: () => void;
  onMenu?: (point: { x: number; y: number }) => void;
  /** Omitted on the finished screen: there is no card to go idle on. */
  secondsLeft?: number;
  /** A grade that did not land (日本-347). Takes the **card row's** note slot and displaces
   *  `Escで終了` — see `noteSlots`, which owns that precedence. */
  notice?: string | null;
}) {
  const finished = position.card === null;
  const countdown = secondsLeft === undefined ? null : idleCountdown(secondsLeft);
  const slots = noteSlots({ notice, countdown, day: position.day, finished });

  if (isPhone) {
    return (
      /* **44px tap targets, and the row is taller for it** (Alex, 11 Aug 2026: "extremely hard to
         tap on mobile"). `✕` and `⋯` were glyph-sized — about 14px of hittable area in the corner
         of the screen, which is roughly a third of the 44px both Apple and Google give as the
         minimum and well under what a thumb can aim at while holding the phone.

         The negative margins are what keep the glyphs where the design puts them: the buttons grow
         *into* the row's 18px gutter rather than pushing the progress bar inward, so the bar and the
         counter do not move and only the height changes. */
      <div className="flex flex-shrink-0 items-center gap-2 px-[18px] py-1">
        <button
          type="button"
          onClick={onEnd}
          aria-label="復習を終了"
          data-check="review.end"
          className="-ml-[13px] flex h-11 w-11 flex-shrink-0 items-center justify-center text-ink-3"
          style={{ fontSize: 18 }}
        >
          ✕
        </button>
        {/* **The two bars stack between `✕` and `⋯`**, which are still 44px targets growing into
            the row's gutter. The block is six pixels taller than the single bar it replaces, and
            that is the whole cost of the change on a phone. */}
        <DayBars
          day={position.day}
          cardNote={slots.cardNote}
          reviewNote={slots.reviewNote}
          compact
        />
        {notice ? (
          <span className="truncate text-meta text-accent-hi" role="status" data-check="review.notice">
            {notice}
          </span>
        ) : (
          countdown && (
            <span className="font-mono text-meta text-accent-hi" data-check="review.countdown">
              {countdown}
            </span>
          )
        )}
        {onMenu && (
          <button
            type="button"
            aria-label="カードの操作"
            data-check="review.card-menu"
            className="-mr-[13px] flex h-11 w-11 flex-shrink-0 items-center justify-center text-ink-3"
            style={{ fontSize: 18 }}
            onClick={(event) => {
              // The menu is pointer-anchored, so it must hang off the BUTTON rather than off the
              // tap point: with a 44px target, `event.clientY` is wherever the thumb landed inside
              // it, and the menu jumped by up to 44px between taps of the same control.
              const rect = event.currentTarget.getBoundingClientRect();
              onMenu({ x: rect.right, y: rect.bottom });
            }}
          >
            ⋯
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-[72px] flex-shrink-0 items-center border-b border-line bg-panel">
      {/* The RULE spans the surface, like every other top bar; what sits on it does not. The bar
          carries progress tracks, which are content rather than chrome — left full-bleed they draw
          a 1600px track above a 1320px card at ⅔ of the ultrawide, and the two read as belonging to
          different screens. `--content-max` is what makes the surface share one, and it is the cap
          the question face, the action bar and `7e` all carry, at every width and on both faces.

          **It stopped following the answer face past `--content-max`, and that is NOT the complaint
          above read backwards** (日本-416). 日本-350 pointed this row at the card's measure, so it
          went to 1760 with it; measured at 1920, revealing then took the two tracks from 1050px to
          1490px — the same fractions drawn 42% longer — and un-revealing took them back. **A row
          whose whole job is to report a quantity may not resize when the quantity has not moved**:
          the fills are percentages of the track (`DayBars`), so a keypress that answered nothing
          lengthened every band on screen. The card region carries no such claim — its width *is*
          the layout, which is why the widening lives there and only there.

          The two shapes are not mirror images either. A capped bar over a wider picture is the
          ordinary order of a page: the narrow row reads as the frame and the wide one as the thing
          inside it, and they stay concentric, which `check-review.mjs` asserts. A bar *wider* than
          the card is the reverse — chrome claiming a canvas the content has not got.

          A side effect worth having: the header is now one width for the whole sitting, `7e`
          included. Grading the last card used to take it 1760 → 1320 at the same instant the card
          was replaced by the finished screen. */}
      <div
        data-check="review.chrome"
        className="mx-auto flex w-full max-w-[var(--content-max)] items-center gap-5 px-[22px]"
      >
        {/* The deck and where you are in this sitting. A fixed column, so the two bars beside it
            start at the same x whatever the deck is called. */}
        <div className="flex w-[118px] flex-shrink-0 flex-col gap-[3px]">
          <span className="truncate text-title font-semibold text-ink">{position.deck}</span>
          <span data-check="review.sitting" className="font-mono text-meta leading-none text-ink-4">
            {finished ? queueSummary(position.counts) : sittingPosition(position.counts)}
          </span>
        </div>
        <DayBars
          day={position.day}
          cardNote={slots.cardNote}
          cardNoteUrgent={slots.cardNoteUrgent}
          reviewNote={slots.reviewNote}
          reviewNoteCountdown={slots.reviewNoteCountdown}
        />
        {/* `Escで終了` is the only thing here that is purely a reminder, which is why it is what
            gives up its room when `記録できませんでした` needs 140px. `noteSlots` owns that call. */}
        {slots.corner && (
          <span className="flex-shrink-0 font-mono text-meta leading-none text-ink-4">
            {slots.corner}
          </span>
        )}
      </div>
    </div>
  );
}

/** A grade key's small print: the arrow and the interval, either of which may be absent — a drill
 *  sends no interval, and a phone has no arrow keys. */
function gradeHint(arrow: string, interval: string, isPhone: boolean): string {
  if (isPhone) return interval;
  return interval ? `${arrow}  ·  ${interval}` : arrow;
}

/**
 * A drill's bar (日本-601, frame D): ドリル, the groups, and the run's own counter.
 *
 * Not `SessionChrome` with parts hidden: that bar's two tracks are today's scheduler figures, and a
 * drill moves none of them. What a drill has is how many cards have passed and how many misses are
 * still to come back.
 */
export function DrillChrome({
  drill,
  isPhone,
  onEnd,
  onMenu,
  notice,
}: {
  drill: DrillProgress;
  isPhone: boolean;
  onEnd: () => void;
  onMenu?: (point: { x: number; y: number }) => void;
  /** A card-menu act that failed — drawn where the counter's repeats sit, as `SessionChrome` does. */
  notice?: string | null;
}) {
  const fill = drill.total > 0 ? Math.min(1, drill.passed / drill.total) : 0;
  return (
    <div
      data-check="drill.chrome"
      className={
        isPhone
          ? "flex flex-shrink-0 items-center gap-2 px-[18px] py-1"
          : "flex h-[72px] flex-shrink-0 items-center border-b border-line bg-panel"
      }
    >
      <div
        className={
          isPhone
            ? "flex min-w-0 flex-1 items-center gap-2"
            : "mx-auto flex w-full max-w-[var(--content-max)] items-center gap-4 px-[22px]"
        }
      >
        <button
          type="button"
          onClick={onEnd}
          aria-label="ドリルを終了"
          data-check="drill.end"
          className={
            isPhone
              ? "-ml-[13px] flex h-11 w-11 flex-shrink-0 items-center justify-center text-ink-3"
              : "flex-shrink-0 text-ink-3"
          }
          style={{ fontSize: 18 }}
        >
          ✕
        </button>
        <span className="flex-shrink-0 rounded-[var(--r-2)] border border-line-strong px-2 py-[2px] text-meta font-semibold text-ink">
          ドリル
        </span>
        {!isPhone && (
          <span className="min-w-0 truncate text-title font-semibold text-ink-2">{drill.title}</span>
        )}
        <span className="flex-1" />
        <span className="relative h-1 w-full max-w-[420px] flex-1 rounded-[var(--r-2)] bg-line">
          <span
            className="absolute inset-y-0 left-0 rounded-[var(--r-2)] bg-ink-3"
            style={{ width: `${fill * 100}%` }}
          />
        </span>
        <span data-check="drill.count" className="flex-shrink-0 font-mono text-meta text-ink-3">
          {drill.passed} / {drill.total}
          {!isPhone && " 合格"}
          {drill.repeats > 0 && <span className="text-accent-text"> · 再出 {drill.repeats}</span>}
        </span>
        {notice && (
          <span className="min-w-0 truncate text-meta text-accent-hi" role="status" data-check="review.notice">
            {notice}
          </span>
        )}
        {onMenu && (
          <button
            type="button"
            aria-label="カードの操作"
            data-check="review.card-menu"
            className={
              isPhone
                ? "-mr-[13px] flex h-11 w-11 flex-shrink-0 items-center justify-center text-ink-3"
                : "flex-shrink-0 text-ink-3"
            }
            style={{ fontSize: 18 }}
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              onMenu({ x: rect.right, y: rect.bottom });
            }}
          >
            ⋯
          </button>
        )}
      </div>
    </div>
  );
}

function GradeButton({
  tone,
  label,
  hint,
  isPhone,
  disabled,
  onClick,
}: {
  tone: "fail" | "pass";
  label: string;
  hint: string;
  isPhone: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const fail = tone === "fail";
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      data-check={fail ? "review.fail" : "review.pass"}
      className="flex flex-1 flex-col items-center justify-center gap-1.5 rounded-[var(--r-4)] border disabled:opacity-60"
      style={{
        height: isPhone ? 72 : 104,
        borderColor: fail ? "var(--accent-line)" : "var(--line-strong)",
        background: fail ? "var(--void)" : "var(--raised)",
      }}
    >
      {/* **合格 is one of the two places `--ink-bright` is spent**, the other being the focused
          headword. THEME.md reserves it and names both; this is a key you press two hundred times
          in a sitting, and one step above bone is what separates it from the chrome around it.
          不可 is the hollow key: no fill, an accent outline, and its label in `--accent-hi`. */}
      <span
        className="font-jp font-semibold"
        style={{
          fontSize: isPhone ? 19 : 25,
          color: fail ? "var(--accent-hi)" : "var(--ink-bright)",
        }}
      >
        {label}
      </span>
      <span
        className="font-mono text-meta"
        style={{ color: fail ? "var(--accent-hi)" : "var(--ink-3)" }}
      >
        {hint}
      </span>
    </button>
  );
}

/**
 * The 簡 seal: the last Pass was recorded as **Easy** (日本-700, option C of its design canvas).
 *
 * It is drawn over the NEXT card, because that is what is on screen when the answer's response
 * arrives. Which is why it sits in the card region's top-right corner, away from the headword, and
 * why it leaves on its own after `EASY_SEAL_MS` — it reports a fact about the card you already left.
 *
 * **Ink, never red.** Red in this app marks what wants you; a promotion wants nothing.
 *
 * - Keyed on the answered count, so a second promotion while one is still up restarts the stamp
 *   with the new figure rather than stacking a second seal.
 * - Cleared by any position that does not carry a promotion — an undo in particular, because the
 *   answer it describes no longer exists.
 * - `prefers-reduced-motion` keeps the seal and drops the stamp (`index.css`).
 */
function EasySeal({ position, isPhone }: { position: ReviewPosition; isPhone: boolean }) {
  const promotion = position.promotion ?? null;
  const key = position.counts.answered;
  const [shown, setShown] = useState<{ key: number; revealMs: number } | null>(null);

  useEffect(() => {
    if (!promotion) {
      setShown(null);
      return;
    }
    setShown({ key, revealMs: promotion.revealMs });
    const timer = window.setTimeout(() => setShown(null), EASY_SEAL_MS);
    return () => window.clearTimeout(timer);
  }, [promotion, key]);

  if (!shown) return null;
  return (
    <span
      key={shown.key}
      role="status"
      aria-label={`簡単として記録 ${easySealSeconds(shown.revealMs)}`}
      data-check="review.easy-seal"
      className="easy-seal pointer-events-none absolute z-20 flex flex-col items-center justify-center rounded-[var(--r-2)] border-[1.5px] border-ink-3"
      style={{
        top: isPhone ? 12 : 18,
        right: isPhone ? 14 : 22,
        width: 52,
        height: 52,
      }}
    >
      <span aria-hidden className="font-jp font-bold leading-none text-ink" style={{ fontSize: 22 }}>
        簡
      </span>
      <span aria-hidden className="mt-1 font-mono leading-none text-ink-4" style={{ fontSize: 10 }}>
        {easySealSeconds(shown.revealMs)}
      </span>
    </span>
  );
}
